import { useEffect, useMemo, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { CheckIcon, PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { Button } from "../../components/base/Button";
import { Table } from "../../components/base/Table/Table";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useSettingDefinitionMutations from "../../data/SettingDefinition/useSettingDefinitionMutations";
import { SettingDefinitionQueryKeys } from "../../data/SettingDefinition/keys";
import { ISettingDefinition } from "../../models/SettingDefinition/SettingDefinition";
import { InputType, SettingType } from "../../models/CustomerSettings/CustomerSetting";
import { CreateUpdateSettingDefinitionModal } from "../../components/SettingDefinitions/CreateUpdateSettingDefinitionModal";
import SettingsTabs from "../../components/Settings/SettingsTabs";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";

const colHelper = createColumnHelper<ISettingDefinition>();

function BoolCell({ value }: { value: boolean }) {
  return value ? (
    <CheckIcon className="w-5 h-5 text-green-600" />
  ) : (
    <XMarkIcon className="w-5 h-5 text-gray-300" />
  );
}

export default function SettingDefinitionsPage() {
  const { setInfo } = usePageNameContext();
  const { deleteHandlers } = useDefaultCRUDHandlers("Setting");
  const { remove } = useSettingDefinitionMutations();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<ISettingDefinition>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingDefinition, setDeletingDefinition] = useState<ISettingDefinition | null>(null);

  const inputTypeOptions = useMemo(() => enumToSelectOptions(InputType), []);
  const settingTypeOptions = useMemo(() => enumToSelectOptions(SettingType), []);

  useEffect(() => {
    setInfo({
      name: "Settings",
      desc: "Manage the catalog of settings available to customers",
      pageTitle: "Settings",
    });
  }, []);

  const handleOpenCreate = () => {
    setEditingDefinition(undefined);
    setModalOpen(true);
  };

  const handleOpenEdit = (definition: ISettingDefinition) => {
    setEditingDefinition(definition);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingDefinition(undefined);
  };

  const handleDelete = (definition: ISettingDefinition) => {
    setDeletingDefinition(definition);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deletingDefinition) return;

    remove.mutate(deletingDefinition.id, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        setDeletingDefinition(null);
      },
    });
  };

  const cols = [
    colHelper.accessor("key", { header: "Key" }),
    colHelper.accessor("label", {
      header: "Label",
      cell: (info) => info.getValue() || "-",
    }),
    colHelper.accessor("inputType", {
      header: "Input Type",
      cell: (info) => inputTypeOptions[info.getValue()]?.label ?? info.getValue(),
    }),
    colHelper.accessor("settingType", {
      header: "Setting Type",
      cell: (info) => settingTypeOptions[info.getValue()]?.label ?? info.getValue(),
    }),
    colHelper.accessor("defaultVisibleToCustomer", {
      header: "Visible (default)",
      cell: (info) => <BoolCell value={info.getValue()} />,
    }),
    colHelper.accessor("required", {
      header: "Required",
      cell: (info) => <BoolCell value={info.getValue()} />,
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => {
        const definition = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1 rounded hover:text-blue-700"
              onClick={() => handleOpenEdit(definition)}
              title="Edit setting"
            >
              <PencilIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
            <button
              type="button"
              className="p-1 rounded hover:text-red-700"
              onClick={() => handleDelete(definition)}
              disabled={remove.isPending}
              title="Delete setting"
            >
              <TrashIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
          </div>
        );
      },
    }),
  ];

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <SettingsTabs />

      <CreateUpdateSettingDefinitionModal
        open={modalOpen}
        setOpen={handleCloseModal}
        definition={editingDefinition}
      />

      <ConfirmModal
        open={deleteOpen}
        setOpen={(open: boolean) => {
          setDeleteOpen(open);
          if (!open) setDeletingDefinition(null);
        }}
        title={"Delete Setting?"}
        message={`Are you sure you want to delete "${deletingDefinition?.key ?? ""}"?\n This is a soft delete.`}
        confirmButtonText={"Delete"}
        onConfirm={handleConfirmDelete}
        isLoading={remove.isPending}
      />

      <div className="flex justify-end">
        <Button
          onClick={handleOpenCreate}
          size="sm"
          className="flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5 -ml-1" />
          <p>Add Setting</p>
        </Button>
      </div>

      <Table<ISettingDefinition>
        url="/settingDefinition"
        queryKey={SettingDefinitionQueryKeys.all()}
        columns={cols}
      />
    </div>
  );
}
